import {Box, Button, CircularProgress, createFilterOptions, Divider, Paper, SvgIcon, TextField} from "@mui/material";
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {InventoryTransferBatchInformationItemsDataGridColumnNames} from "../../../../../../../../core/constants/table/data-grid-column-keys";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../../packages/data-grid";
import {OutlinedInput} from "../../../../../../../components/base/input/outlined_input";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../core/services/api/api";
import {Row} from "reactstrap";
import IconButton from "../../../../../../../components/base/button/icon_button";
import {ReactComponent as RemoveIcon} from "../../../../../../../../assets/images/remove.svg";
import {Switch} from "../../../../../../../components/base/switch";
import {AutoFixHigh, Edit} from "@mui/icons-material";
import classnames from "classnames";
import {deepCopy, formatMoney} from "../../../../../../../../core/services/utils/utils";
import SearchAutoComplete from "../../../../../../../components/base/auto_complete/search_auto_complete";
import PartnerDataGrid from "../../../../../../../containers/data-grid";
import ProductInformationPopover from "../../../../../../../components/app-specific/draggable-popover/product-information-popover";
import TransferItemCommitEditor from "./transfer-item-commit-editor";
import {InventoryTransferStatuses} from "../../../../../../../../core/constants/enums";
import SaveOrRollback from "../../../../../../../components/app-specific/save-or-rollback";
import ImportTransferPartsWithAIDialog from "../../../../../../../components/dialogs/import-transfer-parts-with-ai-dialog";
import {Trans, useTranslation} from "react-i18next";
import {ReactComponent as SaveIcon} from "../../../../../../../../assets/images/save-rollback/check.svg";
import {ReactComponent as CancelIcon} from "../../../../../../../../assets/images/save-rollback/cancel.svg";

const filter = createFilterOptions();

const ADD_ITEM_ROW_ID = '__add-item';

const initialNewItem = {
    product: null,
    quantity: '1',
};

const initialImportWithAiDialog = {
    open: false,
    onImport: () => void 0,
    close: () => void 0,
};

/**
 * Merges parts returned from AddPartsToTransfer into the current items list.
 */
const mergeApiPartsIntoItems = (currentItems, apiParts) => {
    const mergedItems = [...currentItems];
    const existingIds = new Set(currentItems.map((entry) => entry.id));
    apiParts.forEach((apiPart) => {
        const existingIndex = mergedItems.findIndex(
            (entry) => entry.id === apiPart.id || entry.partId === apiPart.partId,
        );
        if (existingIndex >= 0) {
            mergedItems[existingIndex] = apiPart;
        } else if (!existingIds.has(apiPart.id)) {
            mergedItems.push(apiPart);
            existingIds.add(apiPart.id);
        }
    });
    return mergedItems;
};

const InventoryTransferBatchInformationViewItems = ({
                                                        transfer,
                                                        loading,
                                                        setTransfer,
                                                        onItemsChanged,
                                                    }) => {
    const {t} = useTranslation('views', {keyPrefix: 'inventory-transfer.batch-information.items'});
    const [addingPart, setAddingPart] = useState(false);
    const [updatingItemIds, setUpdatingItemIds] = useState([]);
    const [removingItemIds, setRemovingItemIds] = useState([]);
    const [togglingItemsLocationRequired, setTogglingItemsLocationRequired] = useState([]);
    const [newItem, setNewItem] = useState(initialNewItem);
    const [importWithAiDialog, setImportWithAiDialog] = useState(initialImportWithAiDialog);
    const [filterQuery, setFilterQuery] = useState('');
    const [productAvailabilities, setProductAvailabilities] = useState({});
    const productAvailabilitiesRef = useRef(productAvailabilities);
    productAvailabilitiesRef.current = productAvailabilities;
    const isMounted = useIsMounted();

    const isReadOnly = useMemo(
        () => transfer?.transferStatusName === InventoryTransferStatuses.posted
            || transfer?.transferStatusName === InventoryTransferStatuses.finalized,
        [transfer?.transferStatusName],
    );

    const canCommitItems = useMemo(
        () => transfer?.transferStatusName === InventoryTransferStatuses.scanning,
        [transfer?.transferStatusName],
    );

    /**
     * Products can only be added while the transfer is still in Pending.
     */
    const canAddProducts = useMemo(
        () => transfer?.transferStatusName === InventoryTransferStatuses.pending,
        [transfer?.transferStatusName],
    );

    const partIds = useMemo(() => transfer?.items?.map((entry) => entry.partId) ?? [], [transfer?.items]);

    /**
     * Fetches and caches product availabilities for the given part numbers.
     */
    const getProductsAvailabilities = useCallback(async (partNos) => {
        if (!partNos?.length) {
            return;
        }
        setProductAvailabilities((prevState) => {
            const nextState = {...prevState};
            partNos.forEach((partNo) => {
                nextState[partNo] = {
                    ...(nextState[partNo] ?? {}),
                    loading: true,
                };
            });
            return nextState;
        });
        const response = await Api.getProductAvailabilitiesV2({
            partNumbers: partNos,
        });
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag) {
            setProductAvailabilities((prevState) => {
                const nextState = {...prevState};
                partNos.forEach((partNo) => {
                    const data = response.data?.find((entry) => entry.partNumber === partNo);
                    nextState[partNo] = {
                        ...(nextState[partNo] ?? {}),
                        availability: data?.localWarehouses ?? [],
                    };
                });
                return nextState;
            });
        }
        setProductAvailabilities((prevState) => {
            const nextState = {...prevState};
            partNos.forEach((partNo) => {
                nextState[partNo] = {
                    ...(nextState[partNo] ?? {}),
                    loading: false,
                };
            });
            return nextState;
        });
    }, [isMounted]);

    /**
     * Syncs availability cache when transfer items change — fetches new parts, removes stale entries.
     */
    useEffect(() => {
        const partNos = Array.from(new Set(transfer?.items?.map((entry) => entry.partNo) ?? []));
        const cachedAvailabilities = deepCopy(productAvailabilitiesRef.current);

        Object.keys(cachedAvailabilities ?? {}).forEach((partNo) => {
            if (!partNos.includes(partNo)) {
                delete cachedAvailabilities[partNo];
            }
        });

        const newPartNos = partNos.filter((partNo) => cachedAvailabilities[partNo] === undefined);
        if (newPartNos.length) {
            void getProductsAvailabilities(newPartNos);
        }

        if (Object.keys(cachedAvailabilities ?? {}).length !== Object.keys(productAvailabilitiesRef.current ?? {}).length) {
            setProductAvailabilities(cachedAvailabilities);
        }
    }, [getProductsAvailabilities, transfer?.items]);

    /**
     * Merges an updated item into the transfer state after commit/uncommit.
     */
    const handleItemUpdated = useCallback((updatedItem) => {
        setTransfer((prevState) => ({
            ...prevState,
            items: prevState.items.map((entry) => entry.id === updatedItem.id ? updatedItem : entry),
        }));
        onItemsChanged?.();
    }, [onItemsChanged, setTransfer]);

    /**
     * Applies AddPartsToTransfer response parts to transfer state.
     */
    const applyAddedParts = useCallback((apiParts) => {
        if (!apiParts?.length) {
            return;
        }
        setTransfer((prevState) => ({
            ...prevState,
            items: mergeApiPartsIntoItems(prevState.items ?? [], apiParts),
        }));
        onItemsChanged?.();
    }, [onItemsChanged, setTransfer]);

    const dataGridSaveConfig = useMemo(() => {
        if (!isReadOnly) {
            return {
                version: 2,
                name: 'inventoryTransferBatchInformationItems',
            };
        }
        return {
            version: 2,
            name: 'inventoryTransferBatchInformationItemsClosed',
        };
    }, [isReadOnly]);

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...(
                !isReadOnly
                    ? [
                        {
                            name: InventoryTransferBatchInformationItemsDataGridColumnNames.remove,
                            alignment: DataGridColumnAlignments.center,
                            type: DataGridColumnTypes.element,
                            pinned: true,
                            sortable: false,
                            pinnedToggleable: false,
                            resizable: false,
                            visibilityToggleable: false,
                            width: 0,
                        },

                    ]
                    : []
            ),
            {
                title: t('columns.locateRequired'),
                name: InventoryTransferBatchInformationItemsDataGridColumnNames.locateRequired,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 120,
            },
            {
                title: t('columns.partNo'),
                name: InventoryTransferBatchInformationItemsDataGridColumnNames.partNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 180,
            },
            {
                title: t('columns.requestedQty'),
                name: InventoryTransferBatchInformationItemsDataGridColumnNames.requestedQty,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
            },
            {
                title: t('columns.fromWarehouseAvailability', {warehouse: transfer?.fromWarehouseCode ?? ''}),
                name: InventoryTransferBatchInformationItemsDataGridColumnNames.fromWarehouseAvailability,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 150,
            },
            {
                title: t('columns.toWarehouseAvailability', {warehouse: transfer?.toWarehouseCode ?? ''}),
                name: InventoryTransferBatchInformationItemsDataGridColumnNames.toWarehouseAvailability,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 150,
            },
            {
                title: t('columns.committedSerials'),
                name: InventoryTransferBatchInformationItemsDataGridColumnNames.committedSerials,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 400,
            },
            {
                title: t('columns.cost'),
                name: InventoryTransferBatchInformationItemsDataGridColumnNames.cost,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
            },
            {
                title: t('columns.totalCost'),
                name: InventoryTransferBatchInformationItemsDataGridColumnNames.totalCost,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.money,
                sortable: false,
                width: 150,
            },
            {
                title: t('columns.totalScannedCount'),
                name: InventoryTransferBatchInformationItemsDataGridColumnNames.totalScannedCount,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.number,
                width: 150,
            },
            {
                title: t('columns.totalDeliveredCount'),
                name: InventoryTransferBatchInformationItemsDataGridColumnNames.totalDeliveredCount,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.number,
                width: 160,
            },
            {
                title: t('columns.note'),
                name: InventoryTransferBatchInformationItemsDataGridColumnNames.note,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 400,
            },
        ], [isReadOnly, t, transfer?.fromWarehouseCode, transfer?.toWarehouseCode])

    const [dataGridColumns, dataGridSortBy, , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
        sortByObjectMap: {
            property: 'field',
            descending: 'descending',
        },
    });

    const [orderBy, setOrderBy] = useState(dataGridSortBy);

    const sortedItems = useMemo(() => {
        if (!orderBy) {
            return transfer?.items ?? [];
        }
        return [...(transfer?.items ?? [])].sort((a, b) => {
            switch (orderBy.field) {
                case InventoryTransferBatchInformationItemsDataGridColumnNames.locateRequired:
                    if (orderBy.descending)
                        return Number(a.locateRequired) - Number(b.locateRequired);
                    return Number(b.locateRequired) - Number(a.locateRequired);
                case InventoryTransferBatchInformationItemsDataGridColumnNames.partNo:
                    if (orderBy.descending)
                        return a.partNo.localeCompare(b.partNo);
                    return b.partNo.localeCompare(a.partNo);
                case InventoryTransferBatchInformationItemsDataGridColumnNames.totalScannedCount:
                    if (orderBy.descending)
                        return a.totalScannedCount - b.totalScannedCount;
                    return b.totalScannedCount - a.totalScannedCount;
                case InventoryTransferBatchInformationItemsDataGridColumnNames.totalDeliveredCount:
                    if (orderBy.descending)
                        return a.totalDeliveredCount - b.totalDeliveredCount;
                    return b.totalDeliveredCount - a.totalDeliveredCount;
                default:
                    return 0;
            }
        });
    }, [orderBy, transfer?.items]);

    /**
     * Locally filters items by part number, committed serials, and note.
     * Only applied outside the add (Pending) mode, where the top input is a search box.
     */
    const filteredItems = useMemo(() => {
        const query = filterQuery.trim().toLowerCase();
        if (canAddProducts || !query) {
            return sortedItems;
        }
        return sortedItems.filter((item) => {
            const partNoMatch = item.partNo?.toLowerCase().includes(query);
            const noteMatch = item.note?.toLowerCase().includes(query);
            const serialMatch = item.committedSerials?.some(
                (serial) => serial.serialNumber?.toLowerCase().includes(query),
            );
            return partNoMatch || noteMatch || serialMatch;
        });
    }, [canAddProducts, filterQuery, sortedItems]);

    const gridItems = useMemo(() => {
        if (!canAddProducts || addingPart) {
            return filteredItems;
        }
        return [...filteredItems, {id: ADD_ITEM_ROW_ID}];
    }, [addingPart, canAddProducts, filteredItems]);

    /**
     * Toggles the location required for the item.
     */
    const toggleLocationRequired = useCallback(async (item, value) => {
        setTogglingItemsLocationRequired(prevState => [...prevState, item.id]);
        const response = await Api.updateInventoryTransferItemBasicInfo({
            transferId: transfer.id,
            itemId: item.id,
            locateRequired: value,
            updatedProperties: ['LocateRequired'],
        });
        if (!isMounted()) {
            return;
        }
        setTogglingItemsLocationRequired(prevState => prevState.filter(e => e !== item.id));
        if (response?.resultFlag) {
            setTransfer(prevState => ({
                ...prevState,
                items: prevState.items.map(e => e.id === item.id ? {...e, locateRequired: value} : e),
            }));
        }
    }, [isMounted, setTransfer, transfer?.id]);

    /**
     * Updates the requested quantity for an item.
     */
    const updateQty = useCallback(async (item, qty) => {
        setUpdatingItemIds(prevState => [...prevState, item.id]);
        const response = await Api.updateInventoryTransferItemBasicInfo({
            transferId: transfer.id,
            itemId: item.id,
            requestedQty: parseFloat(qty),
            updatedProperties: ['RequestedQty'],
        });
        if (!isMounted()) {
            return;
        }
        setUpdatingItemIds(prevState => prevState.filter(e => e !== item.id));
        if (response?.resultFlag) {
            setTransfer(prevState => ({
                ...prevState,
                items: prevState.items.map(e => e.id === item.id ? {...e, requestedQty: parseFloat(qty)} : e),
            }));
        }
        return response?.resultFlag;
    }, [isMounted, setTransfer, transfer?.id]);

    /**
     * Updates the cost of the item.
     */
    const updateCost = useCallback(async (item, cost) => {
        setUpdatingItemIds(prevState => [...prevState, item.id]);
        const response = await Api.updateInventoryTransferItemCoreInfo({
            transferId: transfer.id,
            itemId: item.id,
            cost: parseFloat(cost),
        });
        if (!isMounted()) {
            return;
        }
        setUpdatingItemIds(prevState => prevState.filter(e => e !== item.id));
        if (response?.resultFlag) {
            setTransfer(prevState => ({
                ...prevState,
                items: prevState.items.map(e => e.id === item.id ? {...e, cost: parseFloat(cost)} : e),
            }));
        }
        return response?.resultFlag;
    }, [isMounted, setTransfer, transfer?.id]);

    /**
     * Updates the note on an item.
     */
    const updateNote = useCallback(async (item, note) => {
        setUpdatingItemIds(prevState => [...prevState, item.id]);
        const normalizedNote = note?.trim() ?? '';
        const response = await Api.updateInventoryTransferItemBasicInfo({
            transferId: transfer.id,
            itemId: item.id,
            note: normalizedNote || null,
            updatedProperties: ['Note'],
        });
        if (!isMounted()) {
            return;
        }
        setUpdatingItemIds(prevState => prevState.filter(e => e !== item.id));
        if (response?.resultFlag) {
            setTransfer(prevState => ({
                ...prevState,
                items: prevState.items.map(e => e.id === item.id ? {...e, note: normalizedNote || null} : e),
            }));
        }
        return response?.resultFlag;
    }, [isMounted, setTransfer, transfer?.id]);

    /**
     * Removes the item from the transfer.
     */
    const removeItem = useCallback(async (item) => {
        setRemovingItemIds(prevState => [...prevState, item.id]);
        // If the item has committed serials, uncommit them all first so the row can be removed.
        const committedSerials = item?.committedSerials ?? [];
        if (committedSerials.length > 0) {
            const uncommitResponse = await Api.uncommitInventoryTransferItem({
                transferId: transfer.id,
                itemId: item.id,
                committedQty: committedSerials.length,
                serialNumbers: committedSerials.map(serial => serial.serialNumber),
            });
            if (!isMounted()) {
                return;
            }
            if (!uncommitResponse?.resultFlag) {
                setRemovingItemIds(prevState => prevState.filter(e => e !== item.id));
                return;
            }
        }
        const response = await Api.removePartsFromInventoryTransfer({
            transferId: transfer.id,
            parts: [{
                partId: item.partId,
                qty: item.requestedQty,
            }],
        });
        if (!isMounted()) {
            return;
        }
        setRemovingItemIds(prevState => prevState.filter(e => e !== item.id));
        if (response?.resultFlag) {
            setTransfer(prevState => ({
                ...prevState,
                items: prevState.items.filter(e => e.id !== item.id),
            }));
            onItemsChanged?.();
        }
    }, [isMounted, onItemsChanged, setTransfer, transfer?.id]);

    /**
     * Gets user confirmation before removing an item.
     */
    const getRemoveConfirmation = useCallback((item) => {
        const committedSerialsCount = item?.committedSerials?.length ?? 0;
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    <Trans
                        t={t}
                        i18nKey={'removeConfirmation'}
                        values={{partNo: item?.partNo ?? ''}}
                        components={{
                            partNo: (
                                <ProductInformationPopover
                                    partNo={item?.partNo}
                                    wrapperProps={{className: 'd-inline-block'}}
                                />
                            ),
                        }}
                    />
                </p>
                {
                    committedSerialsCount > 0 &&
                    <p className={'text-sm text-color-2 mt-2'}>
                        {t('removeConfirmationSerialsWarning', {count: committedSerialsCount})}
                    </p>
                }
            </div>,
            {
                maxWidth: 'sm',
            },
            (confirmed) => confirmed && removeItem(item),
        );
    }, [removeItem, t]);

    /**
     * Adds one or more parts to the transfer.
     */
    const addParts = useCallback(async (parts) => {
        if (!parts?.length) {
            return false;
        }
        setAddingPart(true);
        const response = await Api.addPartsToInventoryTransfer({
            transferId: transfer?.id,
            parts,
        });
        if (!isMounted()) {
            return false;
        }
        setAddingPart(false);
        if (response?.resultFlag) {
            applyAddedParts(response.data?.parts ?? []);
        }
        return response?.resultFlag;
    }, [applyAddedParts, isMounted, transfer?.id]);

    /**
     * Adds a single part from the top search autocomplete.
     */
    const addItemFromSearch = useCallback(async (part) => {
        if (!part?.id) {
            return;
        }
        await addParts([{partId: part.id, qty: 1}]);
    }, [addParts]);

    /**
     * Adds a part from the inline new-row editor.
     */
    const addNewItemToTransfer = useCallback(async (setLoading) => {
        const qty = Number(newItem.quantity ?? 0);
        if (!newItem.product?.id || !qty || Number.isNaN(qty)) {
            return;
        }
        setLoading?.(true);
        const success = await addParts([{partId: newItem.product.id, qty}]);
        if (!isMounted()) {
            return;
        }
        setLoading?.(false);
        if (success) {
            setNewItem(initialNewItem);
        }
    }, [addParts, isMounted, newItem.product?.id, newItem.quantity]);

    /**
     * Resolves part numbers to ids and adds imported lines to the transfer.
     */
    const importProductsToTransfer = useCallback(async (products) => {
        if (!products?.length) {
            return;
        }
        const partNumbers = [...new Set(products.map((entry) => entry.partNo).filter(Boolean))];
        const partInfoResponse = await Api.getPartInfoWithPartNumber({partNumbers});
        if (!isMounted() || !partInfoResponse?.resultFlag) {
            return;
        }
        const partIdByNumber = Object.fromEntries(
            (partInfoResponse.data ?? []).map((entry) => [entry.partNo, entry.id]),
        );
        const mergedByPartId = {};
        products.forEach((entry) => {
            const partId = partIdByNumber[entry.partNo];
            if (!partId) {
                return;
            }
            mergedByPartId[partId] = (mergedByPartId[partId] ?? 0) + Number(entry.quantity ?? 0);
        });
        const parts = Object.entries(mergedByPartId).map(([partId, qty]) => ({
            partId: Number(partId),
            qty,
        }));
        await addParts(parts);
    }, [addParts, isMounted]);

    /**
     * Opens the import-with-AI dialog.
     */
    const openImportWithAiDialog = useCallback(() => {
        setImportWithAiDialog({
            open: true,
            close: () => setImportWithAiDialog(initialImportWithAiDialog),
            onImport: (products) => importProductsToTransfer(products),
        });
    }, [importProductsToTransfer]);

    /**
     * Handles product selection in the inline add row.
     */
    const onNewItemProductSelected = useCallback((part) => {
        setNewItem((prevState) => ({
            ...prevState,
            product: part,
        }));
    }, []);

    /**
     * Handles quantity changes in the inline add row.
     */
    const onNewItemQuantityChanged = useCallback((event) => {
        const value = event.target.value;
        const parsed = Number(value);
        if (!value || Number.isNaN(parsed)) {
            setNewItem((prevState) => ({...prevState, quantity: null}));
            return;
        }
        setNewItem((prevState) => ({...prevState, quantity: Math.max(0, parsed)}));
    }, []);

    /**
     * Commits inline cell edits.
     */
    const onCellEdited = useCallback(async (prevValue, newValue, rowKey, colName) => {
        switch (colName) {
            case InventoryTransferBatchInformationItemsDataGridColumnNames.requestedQty:
                return updateQty(transfer?.items?.find(e => e.id === rowKey), newValue);
            case InventoryTransferBatchInformationItemsDataGridColumnNames.cost:
                return updateCost(transfer?.items?.find(e => e.id === rowKey), newValue);
            case InventoryTransferBatchInformationItemsDataGridColumnNames.note:
                return updateNote(transfer?.items?.find(e => e.id === rowKey), newValue);
            default:
                return;
        }
    }, [transfer?.items, updateCost, updateNote, updateQty]);

    const canSaveNewItem = useMemo(() => {
        const qty = Number(newItem.quantity ?? 0);
        return !!newItem.product?.id && !Number.isNaN(qty) && qty > 0;
    }, [newItem.product?.id, newItem.quantity]);

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return gridItems?.map(item => {
            const isNewItemRow = item.id === ADD_ITEM_ROW_ID;
            const removing = removingItemIds.includes(item.id);
            const updating = updatingItemIds.includes(item.id);
            const quantityEditable = !removing && !updating;
            const costEditable = !removing && !updating;
            const noteEditable = !removing && !updating;
            // Requested qty cannot drop below what is already committed, and must be at least 1.
            const committedQty = Math.max(item?.committedSerials?.length ?? 0, item?.totalScannedCount ?? 0);
            const minRequestedQty = Math.max(1, committedQty);
            const availabilityLoading = productAvailabilities[item.partNo]?.loading;
            const fromWarehouseAvailableQty = productAvailabilities[item.partNo]?.availability
                ?.find((entry) => entry.warehouseCode === transfer?.fromWarehouseCode)?.availableQty;
            const toWarehouseAvailableQty = productAvailabilities[item.partNo]?.availability
                ?.find((entry) => entry.warehouseCode === transfer?.toWarehouseCode)?.availableQty;

            if (isNewItemRow) {
                return {
                    key: item.id,
                    data: item,
                    cells: {
                        [InventoryTransferBatchInformationItemsDataGridColumnNames.remove]:
                            <SaveOrRollback
                                column={false}
                                showText={false}
                                loading={addingPart}
                                show={{save: canSaveNewItem}}
                                onSave={addNewItemToTransfer}
                            />,
                        [InventoryTransferBatchInformationItemsDataGridColumnNames.locateRequired]: null,
                        [InventoryTransferBatchInformationItemsDataGridColumnNames.partNo]:
                            <PartSearchAutoComplete
                                partIds={partIds}
                                onPartSelected={onNewItemProductSelected}
                                loading={addingPart}
                                variant={'outlined'}
                                label={t('product')}
                                size={'small'}
                                paper={false}
                                searchIcon={false}
                                className={'mr-1'}
                            />,
                        [InventoryTransferBatchInformationItemsDataGridColumnNames.fromWarehouseAvailability]: null,
                        [InventoryTransferBatchInformationItemsDataGridColumnNames.toWarehouseAvailability]: null,
                        [InventoryTransferBatchInformationItemsDataGridColumnNames.note]: null,
                        [InventoryTransferBatchInformationItemsDataGridColumnNames.requestedQty]:
                            <TextField
                                fullWidth
                                label={t('quantity')}
                                size={'small'}
                                value={newItem.quantity?.toString() ?? ''}
                                onChange={onNewItemQuantityChanged}
                                type={'number'}
                                inputProps={{min: 1}}
                            />,
                    },
                };
            }

            return ({
                key: item.id,
                data: item,
                cells: {
                    [InventoryTransferBatchInformationItemsDataGridColumnNames.remove]:
                        <IconButton
                            onPressed={() => getRemoveConfirmation(item)}
                            disabled={removing}
                            color={'paper.error'}
                            className={'remove-icon'}
                        >
                            {
                                removing
                                    ? <CircularProgress size={20} color={'secondary'}/>
                                    : <SvgIcon component={RemoveIcon} className='icon normal'/>
                            }
                        </IconButton>,
                    [InventoryTransferBatchInformationItemsDataGridColumnNames.locateRequired]:
                        isReadOnly
                            ? (item?.locateRequired ?? false)
                                ? <SaveIcon className={'save-icon-new'}/>
                                : <CancelIcon className={'cancel-icon-new'}/>
                            : <>
                                <Switch
                                    color={'primary'}
                                    checked={item?.locateRequired ?? false}
                                    disabled={togglingItemsLocationRequired.includes(item.id)}
                                    onChange={(checked) => toggleLocationRequired(item, checked)}
                                />
                            </>,
                    [InventoryTransferBatchInformationItemsDataGridColumnNames.partNo]:
                        <ProductInformationPopover partNo={item?.partNo}>
                            {item?.partNo ?? ''}
                        </ProductInformationPopover>,
                    [InventoryTransferBatchInformationItemsDataGridColumnNames.fromWarehouseAvailability]:
                        availabilityLoading
                            ? <CircularProgress size={20} color={'secondary'}/>
                            : <span {...dataGridCellTextProps}>{fromWarehouseAvailableQty ?? '--'}</span>,
                    [InventoryTransferBatchInformationItemsDataGridColumnNames.toWarehouseAvailability]:
                        availabilityLoading
                            ? <CircularProgress size={20} color={'secondary'}/>
                            : <span {...dataGridCellTextProps}>{toWarehouseAvailableQty ?? '--'}</span>,
                    [InventoryTransferBatchInformationItemsDataGridColumnNames.note]: isReadOnly
                        ? (item?.note?.trim() ? item.note : '--')
                        : {
                            value: ({openEditPopover}) => <>
                                <IconButton
                                    color={'secondary'}
                                    onPressed={openEditPopover}
                                    disabled={updating || !noteEditable}
                                >
                                    {
                                        updating
                                            ? <CircularProgress size={20} color={'secondary'}/>
                                            : <Edit className={'icon small'}/>
                                    }
                                </IconButton>
                                <p {...dataGridCellTextProps} className={'ml-2'}>
                                    {item?.note?.trim() ? item.note : '--'}
                                </p>
                            </>,
                            editable: noteEditable,
                            editableValue: item?.note ?? '',
                            EditableContentComponent: <NoteEditor label={t('newNote')}/>,
                        },
                    [InventoryTransferBatchInformationItemsDataGridColumnNames.requestedQty]: {
                        value: ({openEditPopover}) =>
                            <>
                                {
                                    !isReadOnly &&
                                    <IconButton
                                        color={'secondary'}
                                        onPressed={openEditPopover}
                                        disabled={updating || !quantityEditable}
                                    >
                                        {
                                            updating
                                                ? <CircularProgress size={20} color={'secondary'}/>
                                                : <Edit className={"icon small"}/>
                                        }
                                    </IconButton>
                                }
                                <p {...dataGridCellTextProps} className={classnames({'ml-2': !isReadOnly})}>
                                    {item.requestedQty ?? '0'}
                                </p>
                            </>,
                        editable: quantityEditable && !isReadOnly,
                        editableValue: item.requestedQty,
                        EditableContentComponent: <QuantityEditor label={t('newQuantity')} min={minRequestedQty}/>,
                    },
                    [InventoryTransferBatchInformationItemsDataGridColumnNames.cost]: {
                        value: ({openEditPopover}) => <>
                            {
                                !isReadOnly &&
                                <IconButton
                                    color={'secondary'}
                                    onPressed={openEditPopover}
                                    disabled={updating || !costEditable}
                                >
                                    {
                                        updating
                                            ? <CircularProgress size={20} color={'secondary'}/>
                                            : <Edit className={"icon small"}/>
                                    }
                                </IconButton>
                            }
                            <p {...dataGridCellTextProps} className={classnames({'ml-2': !isReadOnly})}>
                                {formatMoney(item.cost ?? '0')}
                            </p>
                        </>,
                        editable: costEditable && !isReadOnly,
                        editableValue: item.cost,
                        EditableContentComponent: <CostEditor label={t('newCost')}/>,
                    },
                    [InventoryTransferBatchInformationItemsDataGridColumnNames.totalCost]: item?.cost * item?.requestedQty,
                    [InventoryTransferBatchInformationItemsDataGridColumnNames.totalScannedCount]: item?.totalScannedCount,
                    [InventoryTransferBatchInformationItemsDataGridColumnNames.totalDeliveredCount]: item?.totalDeliveredCount,
                    [InventoryTransferBatchInformationItemsDataGridColumnNames.committedSerials]:
                        <TransferItemCommitEditor
                            item={item}
                            transferId={transfer?.id}
                            warehouseCode={transfer?.fromWarehouseCode}
                            readonly={!canCommitItems}
                            onItemUpdated={handleItemUpdated}
                            sourceAvailability={fromWarehouseAvailableQty}
                            pending={canAddProducts}
                        />,
                },
            });
        });
    }, [
        addNewItemToTransfer,
        addingPart,
        canSaveNewItem,
        canCommitItems,
        getRemoveConfirmation,
        gridItems,
        handleItemUpdated,
        isReadOnly,
        newItem.quantity,
        onNewItemProductSelected,
        onNewItemQuantityChanged,
        partIds,
        productAvailabilities,
        t,
        togglingItemsLocationRequired,
        transfer?.fromWarehouseCode,
        transfer?.toWarehouseCode,
        transfer?.id,
        toggleLocationRequired,
        removingItemIds,
        updatingItemIds,
        canAddProducts,
    ]);

    return (
        <>
            <Row className='no-gutters w-100 align-items-start justify-content-center'>
                <Paper className='paper w-100 px-3 py-2'>
                    <Row className='no-gutters align-items-center justify-content-between w-100'>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%'}}>
                            {
                                canAddProducts
                                    ? <>
                                        <PartSearchAutoComplete
                                            partIds={partIds}
                                            onPartSelected={addItemFromSearch}
                                            removeAfterSelect
                                            loading={loading || addingPart}
                                            variant={'outlined'}
                                            size={'small'}
                                            label={t('addNewProduct')}
                                            InputProps={{
                                                sx: {
                                                    paddingRight: `8px !important`,
                                                },
                                            }}
                                        />
                                        <Button
                                            disabled={loading || addingPart}
                                            onClick={openImportWithAiDialog}
                                            className={'d-flex align-items-center'}
                                            color={'primary'}
                                            variant={'outlined'}
                                            sx={{
                                                minWidth: 165,
                                                height: 50,
                                            }}
                                        >
                                            <AutoFixHigh sx={{marginInlineEnd: 0.5}} className={'icon small'}/>
                                            {addingPart ? t('adding') : t('importWithAi')}
                                        </Button>
                                    </>
                                    : <TextField
                                        fullWidth
                                        size={'medium'}
                                        variant={'outlined'}
                                        label={t('searchItemsSerialsNotes')}
                                        value={filterQuery}
                                        onChange={(event) => setFilterQuery(event.target.value)}
                                    />
                            }
                        </Box>
                        {
                            canAddProducts &&
                            <Divider className='divider mt-2'/>
                        }
                        <PartnerDataGrid
                            {...dataGridSaveConfig}
                            columns={dataGridColumns}
                            initialLayoutState={{
                                columns: columns,
                            }}
                            hideFooter
                            hideExporting
                            visibleRows={10}
                            minRowHeight={48}
                            rows={dataGridRows}
                            state={{
                                loading: {state: loading || addingPart},
                                sortBy: orderBy,
                                density: dataGridDensity,
                            }}
                            onCellEdited={onCellEdited}
                            onSortByChanged={(_, current) => setOrderBy(current)}
                        />
                        <div className='my-2 w-100'/>
                    </Row>
                </Paper>
            </Row>
            <ImportTransferPartsWithAIDialog
                {...importWithAiDialog}
            />
        </>
    );
};


const QuantityEditor = ({value, setValue, setCanSubmitChanges, label, min = 1}) => {

    const minValue = Math.max(1, Number(min) || 1);

    useLayoutEffect(() => {
        const parsed = Number(value);
        setCanSubmitChanges(!Number.isNaN(parsed) && parsed >= minValue);
    }, [minValue, setCanSubmitChanges, value]);

    const onValueChanged = (nextValue) => {
        const parsed = Number(nextValue);
        if (!nextValue || Number.isNaN(parsed)) {
            return setValue(undefined);
        }
        setValue(Math.max(minValue, parsed));
    };

    return (
        <OutlinedInput
            fullWidth
            label={label}
            size={'small'}
            value={value?.toString() ?? ''}
            onChange={onValueChanged}
            type={'number'}
            inputProps={{
                min: minValue,
            }}
        />
    );
};

const CostEditor = ({value, setValue, setCanSubmitChanges, label}) => {

    useLayoutEffect(() => {
        const parsed = Number(value);
        setCanSubmitChanges(!Number.isNaN(parsed));
    }, [setCanSubmitChanges, value]);

    const onValueChanged = (nextValue) => {
        const parsed = Number(nextValue);
        if (!nextValue || Number.isNaN(parsed)) {
            return setValue(undefined);
        }
        setValue(Math.max(0, parsed));
    };

    return (
        <OutlinedInput
            fullWidth
            label={label}
            size={'small'}
            value={value?.toString() ?? ''}
            onChange={onValueChanged}
            type={'number'}
            InputProps={{
                startAdornment: <span>$</span>,
            }}
            inputProps={{
                min: 0,
            }}
        />
    );
};

const NoteEditor = ({value, setValue, setCanSubmitChanges, label}) => {
    useLayoutEffect(() => {
        setCanSubmitChanges(true);
    }, [setCanSubmitChanges]);

    const onValueChanged = useCallback((event) => {
        setValue(event.target.value);
    }, [setValue]);

    return (
        <TextField
            fullWidth
            label={label}
            size={'small'}
            multiline
            minRows={3}
            value={value?.toString() ?? ''}
            onChange={onValueChanged}
            sx={{
                minWidth: 320,
            }}
        />
    );
};

const PartSearchAutoComplete = ({
                                    partIds,
                                    onPartSelected,
                                    removeAfterSelect = false,
                                    loading: loadingProps,
                                    ...props
                                }) => {
    const [loading, setLoading] = useState(false);
    const [autoCompleteText, setAutoCompleteText] = useState('');
    const [autoCompleteValue, setAutoCompleteValue] = useState(null);
    const [autoCompleteParts, setAutoCompleteParts] = useState([]);
    const isMounted = useIsMounted();

    useEffect(() => {
        if (!autoCompleteText) {
            setAutoCompleteValue(null);
            if (!removeAfterSelect) {
                onPartSelected(null);
            }
        }
    }, [autoCompleteText, onPartSelected, removeAfterSelect]);

    useEffect(() => {
        if (!autoCompleteText?.length || autoCompleteText?.length < 3) {
            return;
        }
        const timer = setTimeout(() => {
            if (!isMounted()) {
                return;
            }
            if (!autoCompleteText?.length || autoCompleteText?.length < 3) {
                return;
            }
            setLoading(true);
            Api.autoCompletePartLookup(autoCompleteText).then((response) => {
                if (!isMounted()) {
                    return;
                }
                if (response?.resultFlag) {
                    setAutoCompleteParts(response.data ?? []);
                }
                setLoading(false);
            });
        }, 500);
        return () => clearTimeout(timer);
    }, [autoCompleteText, isMounted]);

    const onSelectNewPart = (_, part) => {
        if (!part) {
            part = '';
        }
        if (typeof part === 'string') {
            setAutoCompleteText(part);
            return;
        }
        if (typeof part === 'object') {
            if (removeAfterSelect) {
                onPartSelected(part.value);
                setAutoCompleteValue(null);
                setAutoCompleteText('');
            } else {
                onPartSelected(part.value);
                setAutoCompleteText(part.value.partNumber);
                setAutoCompleteValue(part.value);
            }
        }
    };

    const renderAutoCompletePart = (_, option) => {
        if (!option) {
            return '';
        }
        if (typeof option === 'string') {
            return <p className='mx-2 p-2'>{option}</p>;
        }
        return (
            <Row className='no-gutters align-items-center w-100 justify-content-start py-2'>
                <p className='text-left text-sm text-color-1 mr-5'>
                    {option.value?.partNumber}
                </p>
            </Row>
        );
    };

    return (
        <SearchAutoComplete
            value={autoCompleteValue}
            loading={loading || loadingProps}
            onSelect={onSelectNewPart}
            onChange={(e) => setAutoCompleteText(e.target.value)}
            clearOnBlur={removeAfterSelect}
            freeSolo={false}
            type={'text'}
            inputValue={autoCompleteText}
            filterOptions={(options, params) => {
                const filtered = filter(options, params);
                return filtered.filter((option) => !partIds.includes(option.value?.id));
            }}
            options={autoCompleteParts?.map(part => ({
                title: `${part.partNumber}`,
                value: part,
            }))}
            renderOption={renderAutoCompletePart}
            loadingText={
                <div className='loading auto-complete'>
                    <div/>
                    <div/>
                    <div/>
                    <div/>
                </div>
            }
            {...props}
        />
    );
};

export default InventoryTransferBatchInformationViewItems;
